import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  IconButton,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { Close as CloseIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { Unit, CreateUnitDto, UpdateUnitDto } from '../../types/units.types';
import unitsService from '../../services/units.service';
import { useLanguage } from '../../contexts/LanguageContext';

interface UnitDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  unit: Unit | null;
}

const UnitDialog: React.FC<UnitDialogProps> = ({ open, onClose, onSuccess, unit }) => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';

  const [formData, setFormData] = useState({
    unitCode: '',
    unitName: '',
    unitNameAr: '',
    isBaseUnit: false,
    notes: '',
  });

  const [loading, setLoading] = useState(false);

  // توليد كود محلي فقط
  const generateLocalCode = () => {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000);
    return `UNIT-${timestamp}-${random}`;
  };

  useEffect(() => {
    if (unit) {
      setFormData({
        unitCode: unit.unitCode || '',
        unitName: unit.unitName || '',
        unitNameAr: unit.unitNameAr || '',
        isBaseUnit: unit.isBaseUnit || false,
        notes: unit.notes || '',
      });
    } else if (open) {
      // عند فتح Dialog جديد، نولد كود تلقائياً
      setFormData({
        unitCode: generateLocalCode(),
        unitName: '',
        unitNameAr: '',
        isBaseUnit: false,
        notes: '',
      });
    }
  }, [unit, open]);

  const handleGenerateCode = () => {
    setFormData((prev) => ({ ...prev, unitCode: generateLocalCode() }));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      if (unit) {
        const updateData: UpdateUnitDto = {
          unitId: unit.unitId,
          ...formData,
        };
        await unitsService.update(unit.unitId, updateData);
      } else {
        const createData: CreateUnitDto = formData;
        await unitsService.create(createData);
      }
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving unit:', error);
      alert('Failed to save unit');
    } finally {
      setLoading(false);
    }
  };

  const t = {
    addUnit: language === 'ar' ? 'إضافة وحدة' : 'Add Unit',
    editUnit: language === 'ar' ? 'تعديل وحدة' : 'Edit Unit',
    unitCode: language === 'ar' ? 'كود الوحدة' : 'Unit Code',
    unitName: language === 'ar' ? 'اسم الوحدة (EN)' : 'Unit Name (EN)',
    unitNameAr: language === 'ar' ? 'اسم الوحدة (AR)' : 'Unit Name (AR)',
    isBaseUnit: language === 'ar' ? 'وحدة أساسية' : 'Base Unit',
    notes: language === 'ar' ? 'ملاحظات' : 'Notes',
    save: language === 'ar' ? 'حفظ' : 'Save',
    cancel: language === 'ar' ? 'إلغاء' : 'Cancel',
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth dir={isRTL ? 'rtl' : 'ltr'}>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          {unit ? t.editUnit : t.addUnit}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          <Box display="flex" gap={1}>
            <TextField
              fullWidth
              label={t.unitCode}
              name="unitCode"
              value={formData.unitCode}
              onChange={handleChange}
              disabled={!!unit}
            />
            {!unit && (
              <IconButton color="primary" onClick={handleGenerateCode}>
                <RefreshIcon />
              </IconButton>
            )}
          </Box>

          <TextField
            fullWidth
            label={t.unitName}
            name="unitName"
            value={formData.unitName}
            onChange={handleChange}
            required
          />

          <TextField
            fullWidth
            label={t.unitNameAr}
            name="unitNameAr"
            value={formData.unitNameAr}
            onChange={handleChange}
            required
          />

          <FormControlLabel
            control={
              <Checkbox
                name="isBaseUnit"
                checked={formData.isBaseUnit}
                onChange={handleChange}
              />
            }
            label={t.isBaseUnit}
          />

          <TextField
            fullWidth
            label={t.notes}
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            multiline
            rows={3}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t.cancel}</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={loading}>
          {t.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UnitDialog;